import { useCallback, useEffect, useRef } from "react";
import { trackEvent, getSessionId } from "@/hooks/use-page-tracking";

// The three amenities the host cares about seeing engagement on. Keep these
// keys stable — they're the metadata discriminator that the replay story panel
// and the guest timelines use to name each beat.
export type AmenitySubject = "pontoon" | "sauna" | "pool_table";

// Stable event names shared by every engagement surface. These must match the
// server ingest allowlist (/api/analytics/event) and the PROFILE_EVENT_LABELS
// maps in the admin replay/leads pages.
export const ENGAGEMENT_EVENTS = {
  photoZoomed: "photo_zoomed",
  amenityEngaged: "amenity_engaged",
  sectionViewed: "section_viewed",
  descriptionExpanded: "description_expanded",
  amenitiesExpanded: "amenities_expanded",
  reviewRead: "review_read",
} as const;

// Mark a beat as fired at most once per analytics session. Returns true the
// first time (fire the event) and false thereafter. Guard lives in
// sessionStorage keyed by the analytics session id, so it survives in-tab
// reloads/SPA navigation but resets when a new session starts. When
// sessionStorage is unavailable (private mode) it fires once per page load.
function markFiredOncePerSession(key: string): boolean {
  try {
    const full = `${key}_${getSessionId()}`;
    if (sessionStorage.getItem(full)) return false;
    sessionStorage.setItem(full, "1");
  } catch {
    // sessionStorage unavailable — fall through and fire once per page load.
  }
  return true;
}

// Best-effort map a photo's alt/caption text to one of the tracked amenities so
// a zoom beat can name it ("Zoomed the sauna photo"). Returns null for photos
// that aren't one of the three subjects.
export function subjectFromText(text: string | null | undefined): AmenitySubject | null {
  if (!text) return null;
  const t = text.toLowerCase();
  if (t.includes("pontoon")) return "pontoon";
  if (t.includes("sauna")) return "sauna";
  if (t.includes("pool table") || t.includes("game room") || t.includes("billiard")) {
    return "pool_table";
  }
  return null;
}

// Fire a single photo-zoom engagement event. Callers debounce so one pinch /
// double-tap gesture produces exactly one event (see the gallery ZoomableSlide).
export function trackPhotoZoom(opts: {
  index: number;
  alt: string;
  method: "pinch" | "double_tap";
}): void {
  trackEvent(ENGAGEMENT_EVENTS.photoZoomed, {
    index: opts.index,
    alt: (opts.alt ?? "").slice(0, 200),
    subject: subjectFromText(opts.alt),
    method: opts.method,
  });
}

// Fire an amenity-engagement event — guest clicked into / opened the pontoon,
// sauna, or pool table (amenity card, section, dedicated page, or the gallery
// opened at one of those photos).
export function trackAmenityEngaged(
  amenity: AmenitySubject,
  extra?: Record<string, unknown>,
): void {
  trackEvent(ENGAGEMENT_EVENTS.amenityEngaged, { amenity, ...(extra ?? {}) });
}

// Fire a section-in-view event at most once per session per section. The guard
// lives in sessionStorage keyed by the analytics session id, so it survives
// in-tab reloads/SPA navigation but resets when a new session starts.
export function trackSectionViewed(section: AmenitySubject): void {
  if (!markFiredOncePerSession(`llc_section_viewed_${section}`)) return;
  trackEvent(ENGAGEMENT_EVENTS.sectionViewed, { section });
}

// Fire when the guest expands the property description ("Show more"). Once per
// session, on expand only (callers pass the *next* open state so a toggle
// back-and-forth doesn't spam events).
export function trackDescriptionExpanded(): void {
  if (!markFiredOncePerSession("llc_description_expanded")) return;
  trackEvent(ENGAGEMENT_EVENTS.descriptionExpanded, {});
}

// Fire when the guest opens the full amenities list ("Show all amenities").
// Once per session, on expand only.
export function trackAmenitiesExpanded(): void {
  if (!markFiredOncePerSession("llc_amenities_expanded")) return;
  trackEvent(ENGAGEMENT_EVENTS.amenitiesExpanded, {});
}

// Hook that hands out ref callbacks for amenity sections. Attaching a returned
// callback to an element wires a lightweight IntersectionObserver that fires a
// (de-duped) section_viewed event the first time that element scrolls ~40% into
// view, then disconnects. Observers are cleaned up on unmount.
export function useAmenitySectionViews() {
  const observersRef = useRef<Map<AmenitySubject, IntersectionObserver>>(new Map());

  useEffect(() => {
    const observers = observersRef.current;
    return () => {
      observers.forEach((o) => o.disconnect());
      observers.clear();
    };
  }, []);

  return useCallback(
    (section: AmenitySubject) => (node: HTMLElement | null) => {
      const existing = observersRef.current.get(section);
      if (existing) {
        existing.disconnect();
        observersRef.current.delete(section);
      }
      if (!node || typeof IntersectionObserver === "undefined") return;
      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              trackSectionViewed(section);
              obs.disconnect();
              observersRef.current.delete(section);
              break;
            }
          }
        },
        { threshold: 0.4 },
      );
      obs.observe(node);
      observersRef.current.set(section, obs);
    },
    [],
  );
}

// How long a review must accumulate on screen before it counts as "read". Reviews
// that merely scroll past quickly stay below this and never fire.
const REVIEW_MIN_DWELL_MS = 2000;

// Per-review dwell state tracked while the component is mounted.
type ReviewDwell = {
  author: string;
  enteredAt: number | null; // timestamp of the current in-view segment, or null
  total: number; // accumulated in-view time across segments (ms)
  fired: boolean; // already reported this mount (in-memory guard)
};

// Hook that hands out ref callbacks for review cards. Attaching a returned
// callback wires an IntersectionObserver that measures how long the card is
// actually on screen; once cumulative dwell passes REVIEW_MIN_DWELL_MS the card
// fires a (once-per-session) review_read beat carrying the review index/author
// and the dwell duration. The beat is flushed when the card scrolls out of view
// and on page hide/unmount so partial reads still count.
export function useReviewReadTracking() {
  const observersRef = useRef<Map<number, IntersectionObserver>>(new Map());
  const statesRef = useRef<Map<number, ReviewDwell>>(new Map());

  const flush = useCallback((index: number) => {
    const st = statesRef.current.get(index);
    if (!st) return;
    if (st.enteredAt != null) {
      st.total += Date.now() - st.enteredAt;
      st.enteredAt = null;
    }
    if (st.fired || st.total < REVIEW_MIN_DWELL_MS) return;
    st.fired = true;
    if (!markFiredOncePerSession(`llc_review_read_${index}`)) return;
    trackEvent(ENGAGEMENT_EVENTS.reviewRead, {
      index,
      author: (st.author ?? "").slice(0, 120),
      dwellMs: Math.round(st.total),
      seconds: Math.max(1, Math.round(st.total / 1000)),
    });
  }, []);

  useEffect(() => {
    const observers = observersRef.current;
    const states = statesRef.current;
    const flushAll = () => states.forEach((_s, i) => flush(i));
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushAll();
    };
    window.addEventListener("pagehide", flushAll);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushAll);
      document.removeEventListener("visibilitychange", onVisibility);
      flushAll();
      observers.forEach((o) => o.disconnect());
      observers.clear();
      states.clear();
    };
  }, [flush]);

  return useCallback(
    (index: number, author: string) => (node: HTMLElement | null) => {
      const existing = observersRef.current.get(index);
      if (existing) {
        existing.disconnect();
        observersRef.current.delete(index);
      }
      if (!node || typeof IntersectionObserver === "undefined") return;
      // Preserve any dwell already accumulated across a re-registration; only
      // seed fresh state the first time we see this review index.
      const prev = statesRef.current.get(index);
      if (prev) {
        prev.author = author;
      } else {
        statesRef.current.set(index, { author, enteredAt: null, total: 0, fired: false });
      }
      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const st = statesRef.current.get(index);
            if (!st) continue;
            if (entry.isIntersecting) {
              if (st.enteredAt == null && !st.fired) st.enteredAt = Date.now();
            } else {
              flush(index);
            }
          }
        },
        { threshold: 0.5 },
      );
      obs.observe(node);
      observersRef.current.set(index, obs);
    },
    [flush],
  );
}
